"use client"

/**
 * Sending photos and videos (boona docs/codebase/attachments/README.md §4): one request per photo;
 * a video in 5 MB pieces that pick up where they stopped after a dropped connection, then the server
 * converts it (status "processing", polled). Files are checked here first (type, size, length) with the
 * limits the server enforces too, and go up one after another.
 *
 * mode "attach": a finished file leaves the tiles and joins the list (onUploaded) — an appointment, a
 * saved note. mode "draft": finished files stay as tiles until the note is saved (readyIds) — a note
 * being written.
 */
import * as React from "react"
import { useTranslation } from "react-i18next"
import {
  deleteAttachment, putPiece, refusal, startVideo, uploadPhoto, uploadState, type UploadTarget,
} from "@/lib/services/attachmentsService"
import { formatDuration, toItem } from "./mapItem"
import { ATTACHMENT_LIMITS, type AttachmentItem, type PendingUpload } from "./types"

const RETRY_DELAYS = [1500, 4000, 10000, 20000]
const POLL_MS = 3000
const POLL_LIMIT_MS = 15 * 60 * 1000

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

function kindOf(file: File): "photo" | "video" | null {
  if (file.type.startsWith("video/") || /\.(mov|mp4|m4v|webm)$/i.test(file.name)) return "video"
  if (file.type.startsWith("image/") || /\.(heic|heif|jpe?g|png|webp)$/i.test(file.name)) return "photo"
  return null
}

function videoSeconds(url: string): Promise<number | null> {
  return new Promise((resolve) => {
    const v = document.createElement("video")
    v.preload = "metadata"
    v.onloadedmetadata = () => resolve(Number.isFinite(v.duration) ? v.duration : null)
    v.onerror = () => resolve(null)
    v.src = url
  })
}

interface Options {
  mode: "attach" | "draft"
  /** Files the note or appointment already has (the 10-file limit counts them) */
  existing: number
  onUploaded?: (item: AttachmentItem) => void
}

export function useAttachmentUploader(target: UploadTarget | null, { mode, existing, onUploaded }: Options) {
  const { t } = useTranslation()
  const [pending, setPending] = React.useState<PendingUpload[]>([])
  const aborts = React.useRef(new Map<string, AbortController>())
  const queue = React.useRef<Promise<void>>(Promise.resolve())
  const onUploadedRef = React.useRef(onUploaded)
  onUploadedRef.current = onUploaded

  const patch = React.useCallback((id: string, p: Partial<PendingUpload>) =>
    setPending((all) => all.map((u) => (u.id === id ? { ...u, ...p } : u))), [])

  const finish = React.useCallback((u: PendingUpload, item: AttachmentItem) => {
    if (mode === "attach") {
      onUploadedRef.current?.(item)
      setPending((all) => all.filter((x) => x.id !== u.id))
      URL.revokeObjectURL(u.previewUrl)
    } else {
      patch(u.id, { state: "ready", progress: 1, serverId: Number(item.id) })
    }
  }, [mode, patch])

  const fail = React.useCallback((id: string, err: any, fallback: string) => {
    const r = refusal(err)
    patch(id, { state: "error", error: r.message || fallback })
  }, [patch])

  const sendPhoto = React.useCallback(async (u: PendingUpload, file: File, signal: AbortSignal) => {
    try {
      const item = await uploadPhoto(target!, file, (p) => patch(u.id, { progress: p }), signal)
      finish(u, toItem(item))
    } catch (err: any) {
      if (signal.aborted) return
      fail(u.id, err, t("attachments.err.upload_failed", "Couldn't upload — try again."))
    }
  }, [target, patch, finish, fail, t])

  const sendVideo = React.useCallback(async (u: PendingUpload, file: File, signal: AbortSignal) => {
    let uploadId: number
    let piece: number
    try {
      const started = await startVideo(target!, file)
      uploadId = started.upload_id
      piece = started.chunk_size
      patch(u.id, { serverId: uploadId })
    } catch (err: any) {
      return fail(u.id, err, t("attachments.err.upload_failed", "Couldn't upload — try again."))
    }
    let offset = 0
    let misses = 0
    while (offset < file.size) {
      if (signal.aborted) return
      const slice = file.slice(offset, Math.min(offset + piece, file.size))
      try {
        const base = offset
        const r = await putPiece(uploadId, offset, slice, (loaded) => patch(u.id, { progress: (base + loaded) / file.size }), signal)
        offset = r.received
        misses = 0
      } catch (err: any) {
        if (signal.aborted) return
        const r = refusal(err)
        if (r.status === 409 && typeof err?.response?.data?.received === "number") {   // resume where the server is
          offset = err.response.data.received
          continue
        }
        if (r.status && r.status < 500 && r.status !== 408 && r.status !== 429) {
          return fail(u.id, err, t("attachments.err.upload_failed", "Couldn't upload — try again."))
        }
        if (misses >= RETRY_DELAYS.length) {
          return fail(u.id, err, t("attachments.err.connection", "The connection dropped — remove the file and try again."))
        }
        await sleep(RETRY_DELAYS[misses++])
        try { offset = (await uploadState(uploadId)).received } catch { /* keep the offset; the PUT says */ }
      }
    }
    patch(u.id, { state: "processing", progress: 1 })
    const until = Date.now() + POLL_LIMIT_MS
    while (Date.now() < until) {
      if (signal.aborted) return
      await sleep(POLL_MS)
      try {
        const s = await uploadState(uploadId)
        if (s.status === "ready" && s.item) return finish(u, toItem(s.item))
        if (s.status === "failed") {
          return patch(u.id, { state: "error", error: t("attachments.err.convert", "This video couldn't be prepared. Try another file.") })
        }
      } catch { /* a blip: ask again */ }
    }
    patch(u.id, { state: "error", error: t("attachments.err.slow", "Still preparing — it will appear when ready.") })
  }, [target, patch, finish, fail, t])

  const add = React.useCallback(async (files: FileList | File[]) => {
    if (!target) return
    let count = existing + pending.filter((u) => u.state !== "error").length
    for (const file of Array.from(files)) {
      const kind = kindOf(file)
      const u: PendingUpload = {
        id: `u${Date.now()}${Math.random().toString(36).slice(2, 7)}`, name: file.name, kind,
        previewUrl: URL.createObjectURL(file), displayable: !/hei[cf]/i.test(file.type + file.name),
        progress: 0, state: "uploading", sizeBytes: file.size,
      }
      count += 1
      if (count > ATTACHMENT_LIMITS.perItem) Object.assign(u, { state: "error", error: t("attachments.err.too_many", "Up to 10 files") })
      else if (!kind) Object.assign(u, { state: "error", error: t("attachments.err.type", "Only photos and videos") })
      else if (kind === "photo" && file.size > ATTACHMENT_LIMITS.photoBytes) Object.assign(u, { state: "error", error: t("attachments.err.photo_size", "Photos up to 20 MB") })
      else if (kind === "video" && file.size > ATTACHMENT_LIMITS.videoBytes) Object.assign(u, { state: "error", error: t("attachments.err.video_size", "Videos up to 250 MB") })
      else if (kind === "video") {
        const secs = await videoSeconds(u.previewUrl)
        if (secs && secs > ATTACHMENT_LIMITS.videoSeconds + 0.5) {
          Object.assign(u, { state: "error", error: t("attachments.err.video_length", "{{length}} — videos up to 2 min", { length: formatDuration(secs) }) })
        } else if (secs) u.duration = formatDuration(secs)
      }
      if (u.state === "error") count -= 1
      setPending((all) => [...all, u])
      if (u.state !== "uploading") continue
      const ctrl = new AbortController()
      aborts.current.set(u.id, ctrl)
      queue.current = queue.current.then(() => (kind === "video" ? sendVideo(u, file, ctrl.signal) : sendPhoto(u, file, ctrl.signal)))
        .finally(() => aborts.current.delete(u.id))
    }
  }, [target, existing, pending, sendPhoto, sendVideo, t])

  const remove = React.useCallback((id: string) => {
    setPending((all) => {
      const u = all.find((x) => x.id === id)
      if (u) {
        aborts.current.get(id)?.abort()
        if (u.serverId && u.state !== "error") deleteAttachment(u.serverId).catch(() => undefined)   // cancel on the server too
        URL.revokeObjectURL(u.previewUrl)
      }
      return all.filter((x) => x.id !== id)
    })
  }, [])

  /** A note being saved: the ids of its finished files (still converting videos count), and clear the tiles */
  const takeReady = React.useCallback((): number[] => {
    const ids = pending.filter((u) => u.serverId && (u.state === "ready" || u.state === "processing")).map((u) => u.serverId!)
    pending.forEach((u) => { aborts.current.get(u.id)?.abort(); URL.revokeObjectURL(u.previewUrl) })
    setPending([])
    return ids
  }, [pending])

  const busy = pending.some((u) => u.state === "uploading")
  return { pending, add, remove, takeReady, busy }
}
